import { select, pie, interpolate, rgb, easeLinear, arc } from "d3";
import { BaseChartComponent } from "../base-chart.component";
import { ChartTooltip } from "../chart-tooltip.component";
import { PieChartLegend } from "./components/pie-chart-legend.component";
import { PieChartEntry } from "./models/pie-chart-entry.model";
import { PieChartData } from "./models/pie-chart-data.model";

export class PieChartComponent extends BaseChartComponent {
    private tooltip: ChartTooltip;
    private legend: PieChartLegend;
    private readonly RADIUS = 100;
    protected chartType = "pie";
    protected _data: PieChartData = null;

    protected initComponent(context: JQuery<HTMLElement>, input: PieChartData): void {
        this._data = input || this._data;
        super.init(context);
    }

    public static init(context: JQuery<HTMLElement>, input: PieChartData): PieChartComponent {
        const component = new PieChartComponent();
        component.initComponent(context, input);
        return component;
    }

    public createChart(): void {
        super.createChart();

        this.chart
            .attr("viewBox", `-${(this.RADIUS * 2) / 2} -${(this.RADIUS * 2) / 2} ${this.RADIUS * 2} ${this.RADIUS * 2}`);

        // Initialize legend with handlers
        this.legend = new PieChartLegend(
            this._currentContext,
            this._data.entries,
            {
                handlers: {
                    onClick: (d: PieChartEntry, el: HTMLElement): void => {
                        d.visible = !d.visible;
                        this.legend.disable(el, !d.visible);
                        this.renderChart();
                    }
                }
            }
        );

        this.tooltip = new ChartTooltip(this._currentContext);
    }

    public renderChart(): void {
        const visibleEntries = (this._data.entries || []).filter(d => d.visible);

        const pieGenerator = pie<PieChartEntry>()
            .sort(null)
            .value(d => d.value);

        let arcGroupsWrap = this.chart
            .selectAll<SVGGElement, unknown>(".pcg-plot__groups")
            .data([null]);

        arcGroupsWrap = arcGroupsWrap
            .enter().append("g")
            .attr("class", "pcg-plot__groups")
            .merge(arcGroupsWrap);

        let arcGroups = arcGroupsWrap
            .selectAll<SVGGElement, [d3.PieArcDatum<PieChartEntry>]>(".pcg-plot__group")
            .data(pieGenerator(visibleEntries).map(d => [d]), d => d[0].index);

        arcGroups.exit().remove();

        arcGroups = arcGroups
            .enter().append("g")
            .attr("class", "pcg-plot__group")
            .merge(arcGroups);

        let paths = arcGroups
            .selectAll<SVGPathElement, d3.PieArcDatum<PieChartEntry>>(".pcg-plot__arc")
            .data(d => d);

        paths.exit().remove();

        paths = paths
            .enter().append("path")
            .attr("class", "pcg-plot__arc")
            .merge(paths);

        const arcGenerator = arc<d3.PieArcDatum<PieChartEntry>>()
            .outerRadius(this.RADIUS)
            .innerRadius(0);

        paths
            .attr("d", arcGenerator)
            .attr("fill", arcData => arcData.data.color)
            .attr("initial-fill", arcData => arcData.data.color)
            .attr("stroke", "white")
            .attr("stroke-width", 1);

        paths
            .on("mouseover", (event: MouseEvent, d: d3.PieArcDatum<PieChartEntry>) => {
                const el = select<SVGPathElement, d3.PieArcDatum<PieChartEntry>>(event.currentTarget as SVGPathElement);
                const currentFill = el.attr("fill");

                if (currentFill) {
                    const hoverColor = rgb(currentFill).brighter(0.1);
                    el.attr("fill", hoverColor.toString());
                    this.tooltip.show(this._data?.title || "", d.data.label || "");
                }
            })
            .on("mousemove", (event: MouseEvent) => {
                this.tooltip.move(event.clientX - 25, event.clientY + 35);
            })
            .on("mouseout", (event: MouseEvent) => {
                const el = select<SVGPathElement, d3.PieArcDatum<PieChartEntry>>(event.currentTarget as SVGPathElement);
                const initialFill = el.attr("initial-fill");

                if (initialFill) {
                    el.attr("fill", initialFill);
                    this.tooltip.hide();
                }
            });

        paths
            .transition()
            .duration(400)
            .ease(easeLinear)
            .attrTween("d", (arcData: d3.PieArcDatum<PieChartEntry>) => {
                const interpolateArc = interpolate({ startAngle: 0, endAngle: 0 }, arcData);
                return (t: number) => {
                    const arcPath = arcGenerator(interpolateArc(t));
                    return arcPath ? arcPath : "";
                };
            });
    }
}
